const parseToTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    let parsed = new Date(trimmed).getTime();
    if (Number.isFinite(parsed)) return parsed;
    if (trimmed.includes(' ')) {
      parsed = new Date(trimmed.replace(' ', 'T')).getTime();
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const getEventEndDateValue = (event = {}) => {
  const rawEndDate = event.endDate ?? event.end_date;
  if (rawEndDate) {
    if (typeof rawEndDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawEndDate.trim())) {
      return event.eventEndTime
        ? `${rawEndDate.trim()}T${event.eventEndTime}:00`
        : `${rawEndDate.trim()}T23:59:59.999`;
    }
    return rawEndDate;
  }

  if (event.eventEndDate) {
    return event.eventEndTime
      ? `${event.eventEndDate}T${event.eventEndTime}:00`
      : `${event.eventEndDate}T23:59:59.999`;
  }

  const rawStartDate = event.startDate ?? event.start_date;
  if (rawStartDate) {
    if (typeof rawStartDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawStartDate.trim())) {
      return event.eventTime
        ? `${rawStartDate.trim()}T${event.eventTime}:00`
        : `${rawStartDate.trim()}T23:59:59.999`;
    }
    return rawStartDate;
  }

  if (event.eventStartDate) {
    return event.eventStartTime
      ? `${event.eventStartDate}T${event.eventStartTime}:00`
      : `${event.eventStartDate}T23:59:59.999`;
  }

  return null;
};

export const isEventEnded = (event = {}, now = Date.now()) => {
  if (!event || typeof event !== 'object') return false;
  const endDateValue = getEventEndDateValue(event);
  if (!endDateValue) return false;

  const endTimestamp = parseToTimestamp(endDateValue);
  return endTimestamp !== null && endTimestamp < now;
};

export const isEventActive = (event = {}, now = Date.now()) => {
  const active = typeof event.active === 'boolean' ? event.active : event.isActive;
  return Boolean(active) && !isEventEnded(event, now);
};

export const parsePendingPayload = (rawPayload) => {
  if (!rawPayload) return {};
  if (typeof rawPayload === 'object') return rawPayload;

  try {
    const parsed = JSON.parse(rawPayload);
    if (typeof parsed === 'string') {
      return JSON.parse(parsed);
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const isDeactivationPayload = (payload, originalEvent = null) => {
  if (!payload || typeof payload !== 'object') return false;

  if (
    payload.isDeactivationRequest === true ||
    payload.isDeactivation === true ||
    payload.hasPendingDeactivation === true ||
    payload.isDeactivated === true ||
    payload.deactivated === true ||
    payload.deactivate === true ||
    payload.isDeactivated === 'true' ||
    payload.deactivated === 'true'
  ) {
    return true;
  }

  const actionStr = String(
    payload.action ||
    payload.actionType ||
    payload.type ||
    payload.requestType ||
    payload.pendingAction ||
    payload.pendingType ||
    payload.pendingEditType ||
    ''
  ).toUpperCase();
  if (
    [
      'DEACTIVATE',
      'DEACTIVATION',
      'DEACTIVATE_EVENT',
      'DEAKTYWACJA',
      'DEZAKTYWACJA',
      'DEACTIVATE_REQUEST',
    ].includes(actionStr)
  ) {
    return true;
  }

  const statusStr = String(
    payload.status ||
    payload.statusName ||
    payload.eventStatus ||
    ''
  ).toUpperCase();
  if (
    [
      'INACTIVE',
      'DEACTIVATED',
      'DISABLED',
      'NOTACTIVE',
      'NOT_ACTIVE',
      'NIEAKTYWNE',
      'NIEAKTYWNY',
    ].includes(statusStr)
  ) {
    return true;
  }

  if (originalEvent) {
    const originalActive =
      typeof originalEvent.active === 'boolean'
        ? originalEvent.active
        : typeof originalEvent.isActive === 'boolean'
        ? originalEvent.isActive
        : originalEvent.isActive !== undefined
        ? Boolean(originalEvent.isActive === true || originalEvent.isActive === 'true' || originalEvent.isActive === 1)
        : undefined;

    const hasNewActiveProp =
      payload.isActive !== undefined ||
      payload.active !== undefined ||
      payload.is_active !== undefined;

    if (hasNewActiveProp) {
      const newActive =
        payload.isActive !== undefined
          ? Boolean(payload.isActive === true || payload.isActive === 'true' || payload.isActive === 1)
          : payload.active !== undefined
          ? Boolean(payload.active === true || payload.active === 'true' || payload.active === 1)
          : Boolean(payload.is_active === true || payload.is_active === 'true' || payload.is_active === 1);

      if (newActive === false) {
        if (originalActive === false) {
          return false;
        }
        return true;
      }
      return false;
    }
  }

  if (payload.hasPendingEdit !== true) {
    if (
      payload.isActive === false ||
      payload.active === false ||
      payload.is_active === false ||
      payload.isActive === 'false' ||
      payload.active === 'false' ||
      payload.is_active === 'false'
    ) {
      return true;
    }
  }

  return false;
};

export const isActivationPayload = (payload, originalEvent = null) => {
  if (!payload || typeof payload !== 'object') return false;
  if (isDeactivationPayload(payload, originalEvent)) return false;

  if (
    payload.isActivationRequest === true ||
    payload.isActivation === true ||
    payload.hasPendingActivation === true ||
    payload.isActivated === true ||
    payload.activated === true ||
    payload.activate === true ||
    payload.isActivated === 'true' ||
    payload.activated === 'true'
  ) {
    return true;
  }

  const actionStr = String(
    payload.action ||
    payload.actionType ||
    payload.type ||
    payload.requestType ||
    payload.pendingAction ||
    payload.pendingType ||
    payload.pendingEditType ||
    ''
  ).toUpperCase();
  if (
    [
      'ACTIVATE',
      'ACTIVATION',
      'ACTIVATE_EVENT',
      'AKTYWACJA',
      'ACTIVATE_REQUEST',
    ].includes(actionStr)
  ) {
    return true;
  }

  const statusStr = String(
    payload.status ||
    payload.statusName ||
    payload.eventStatus ||
    ''
  ).toUpperCase();
  if (
    [
      'ACTIVE',
      'ACTIVATED',
      'ENABLED',
      'AKTYWNE',
      'AKTYWNY',
    ].includes(statusStr)
  ) {
    return true;
  }

  if (originalEvent) {
    const originalActive =
      typeof originalEvent.active === 'boolean'
        ? originalEvent.active
        : typeof originalEvent.isActive === 'boolean'
        ? originalEvent.isActive
        : originalEvent.isActive !== undefined
        ? Boolean(originalEvent.isActive === true || originalEvent.isActive === 'true' || originalEvent.isActive === 1)
        : undefined;

    const hasNewActiveProp =
      payload.isActive !== undefined ||
      payload.active !== undefined ||
      payload.is_active !== undefined;

    if (hasNewActiveProp) {
      const newActive =
        payload.isActive !== undefined
          ? Boolean(payload.isActive === true || payload.isActive === 'true' || payload.isActive === 1)
          : payload.active !== undefined
          ? Boolean(payload.active === true || payload.active === 'true' || payload.active === 1)
          : Boolean(payload.is_active === true || payload.is_active === 'true' || payload.is_active === 1);

      if (originalActive === false && newActive === true) {
        return true;
      }
      return false;
    }
  }

  if (payload.hasPendingEdit !== true) {
    const keys = Object.keys(payload);
    const hasOnlyStatusKeys = keys.every((k) =>
      ['isActive', 'active', 'is_active', 'status', 'action', 'type', 'requestType', 'pendingAction', 'pendingType'].includes(k)
    );
    if (
      hasOnlyStatusKeys &&
      (payload.isActive === true ||
        payload.active === true ||
        payload.is_active === true ||
        payload.isActive === 'true' ||
        payload.active === 'true' ||
        payload.is_active === 'true' ||
        payload.isActive === 1)
    ) {
      return true;
    }
  }

  return false;
};

export const isPendingDeactivation = (pendingItemOrPayload, originalEvent = null) => {
  if (!pendingItemOrPayload) return false;

  if (
    pendingItemOrPayload.isDeactivationRequest === true ||
    pendingItemOrPayload.isDeactivation === true ||
    pendingItemOrPayload.hasPendingDeactivation === true
  ) {
    return true;
  }

  const itemAction = String(
    pendingItemOrPayload.action ||
    pendingItemOrPayload.actionType ||
    pendingItemOrPayload.type ||
    pendingItemOrPayload.requestType ||
    pendingItemOrPayload.pendingAction ||
    pendingItemOrPayload.pendingType ||
    pendingItemOrPayload.pendingEditType ||
    ''
  ).toUpperCase();
  if (
    [
      'DEACTIVATE',
      'DEACTIVATION',
      'DEACTIVATE_EVENT',
      'DEAKTYWACJA',
      'DEZAKTYWACJA',
      'DEACTIVATE_REQUEST',
    ].includes(itemAction)
  ) {
    return true;
  }

  const rawPayload =
    pendingItemOrPayload.pendingChange?.payloadJson ??
    pendingItemOrPayload.payloadJson ??
    pendingItemOrPayload.payload;

  const payload = rawPayload !== undefined ? parsePendingPayload(rawPayload) : pendingItemOrPayload;
  const baseEvent = originalEvent || pendingItemOrPayload.event || pendingItemOrPayload.originalEvent || null;

  return isDeactivationPayload(payload, baseEvent);
};

export const isPendingActivation = (pendingItemOrPayload, originalEvent = null) => {
  if (!pendingItemOrPayload) return false;
  if (isPendingDeactivation(pendingItemOrPayload, originalEvent)) return false;

  if (
    pendingItemOrPayload.isActivationRequest === true ||
    pendingItemOrPayload.isActivation === true ||
    pendingItemOrPayload.hasPendingActivation === true
  ) {
    return true;
  }

  const itemAction = String(
    pendingItemOrPayload.action ||
    pendingItemOrPayload.actionType ||
    pendingItemOrPayload.type ||
    pendingItemOrPayload.requestType ||
    pendingItemOrPayload.pendingAction ||
    pendingItemOrPayload.pendingType ||
    pendingItemOrPayload.pendingEditType ||
    ''
  ).toUpperCase();
  if (
    [
      'ACTIVATE',
      'ACTIVATION',
      'ACTIVATE_EVENT',
      'AKTYWACJA',
      'ACTIVATE_REQUEST',
    ].includes(itemAction)
  ) {
    return true;
  }

  const rawPayload =
    pendingItemOrPayload.pendingChange?.payloadJson ??
    pendingItemOrPayload.payloadJson ??
    pendingItemOrPayload.payload;

  const payload = rawPayload !== undefined ? parsePendingPayload(rawPayload) : pendingItemOrPayload;
  const baseEvent = originalEvent || pendingItemOrPayload.event || pendingItemOrPayload.originalEvent || null;

  return isActivationPayload(payload, baseEvent);
};
